/**
 * Look & feel: DACcelerate's side of the style-guide engine (src/styleguide/).
 *
 * An admin tries a colour, corners, type size, font or depth in the drawer and
 * watches the whole app follow. Nothing is stored and nobody else sees it: the
 * values live in this tab until it reloads. Copy link shares them (an admin who
 * opens it sees the same); Copy theme is the decision, pasted into theme.ts.
 *
 * Fluent sets its tokens on the FluentProvider's own element, so they can't be
 * steered from :root. The engine runs with `root: false`, and `themeFor()`
 * builds the Fluent theme main.tsx hands to the provider. The few colours that
 * live outside Fluent (`bayerColors`, the sidebar, index.css's --brand-*) read
 * CSS variables with today's value as the fallback; `cssVarsFor()` sets them.
 */
import { createLightTheme } from '@fluentui/react-components'
import type { BrandVariants, Theme } from '@fluentui/react-components'
import { bayerBrand, bayerLightTheme } from '../theme'
import { createStyleGuide } from '../styleguide/engine'
import type { Overrides, Preset } from '../styleguide/engine'
import { fromOklch, parseHex, toHex, toOklch } from '../styleguide/color'

/** The search parameter a shared look travels in. */
export const LOOK_PARAM = 'look'

/** Body text size today, in px (Fluent's fontSizeBase300). */
const BASE_TEXT_PX = 14

const SLOTS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160] as const

const FONTS = [
  { id: 'segoe', label: 'Segoe UI', value: bayerLightTheme.fontFamilyBase },
  {
    id: 'variable',
    label: 'Segoe UI Variable',
    value: '"Segoe UI Variable", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, sans-serif',
  },
  { id: 'system', label: 'System', value: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' },
  { id: 'arial', label: 'Arial', value: 'Arial, "Helvetica Neue", Helvetica, sans-serif' },
]

export const lookAndFeel = createStyleGuide({
  prefix: '--dac-',
  root: false,
  groups: [
    {
      title: 'Colour',
      kind: 'color',
      tokens: [
        {
          name: 'brand',
          use: 'Buttons, links, focus, selected tabs; the 16 brand shades follow',
          contrast: 4.5,
        },
        { name: 'sidebar', use: 'Sidebar background; hover and borders follow' },
      ],
    },
    {
      title: 'Corners',
      kind: 'radius',
      tokens: [
        { name: 'radius-md', use: 'Buttons, inputs, cards' },
        { name: 'radius-lg', use: 'Panels, menus' },
        { name: 'radius-xl', use: 'Dialogs' },
      ],
    },
    {
      title: 'Type',
      kind: 'type',
      tokens: [{ name: 'text', use: 'Body text; every Fluent size scales with it', unit: 'px' }],
    },
    { title: 'Font', kind: 'font', tokens: [{ name: 'font', use: 'All text' }] },
    { title: 'Depth', kind: 'shadow', tokens: [{ name: 'depth', use: 'Fluent shadows' }] },
  ],
  fonts: FONTS,
  shadows: [
    { id: 'fluent', label: 'Fluent', values: { depth: '' } },
    { id: 'none', label: 'None', values: { depth: 'none' } },
  ],
  dimensions: [
    {
      id: 'colour',
      label: 'Colour',
      tokens: ['brand'],
      options: [
        { id: 'haze', label: 'Haze', values: {}, swatch: ['4c6a80'] },
        { id: 'bayer', label: 'Bayer blue', values: { brand: '10384f' }, swatch: ['10384f'] },
        { id: 'blue', label: 'Blue', values: { brand: '1f5fa8' }, swatch: ['1f5fa8'] },
        { id: 'teal', label: 'Teal', values: { brand: '0f766e' }, swatch: ['0f766e'] },
        { id: 'graphite', label: 'Graphite', values: { brand: '4b5563' }, swatch: ['4b5563'] },
      ],
    },
    {
      id: 'sidebar',
      label: 'Sidebar',
      tokens: ['sidebar'],
      options: [
        { id: 'slate', label: 'Slate', values: {}, swatch: ['283744'] },
        { id: 'ink', label: 'Ink', values: { sidebar: '1d232a' }, swatch: ['1d232a'] },
        { id: 'bayer', label: 'Bayer blue', values: { sidebar: '10384f' }, swatch: ['10384f'] },
        { id: 'teal', label: 'Teal', values: { sidebar: '134e4a' }, swatch: ['134e4a'] },
      ],
    },
    {
      id: 'corners',
      label: 'Corners',
      tokens: ['radius-md', 'radius-lg', 'radius-xl'],
      options: [
        { id: 'crisp', label: 'Crisp', values: {}, note: '3 / 4 / 6' },
        {
          id: 'sharp',
          label: 'Sharp',
          values: { 'radius-md': '2', 'radius-lg': '2', 'radius-xl': '3' },
          note: '2 / 2 / 3',
        },
        {
          id: 'fluent',
          label: 'Fluent',
          values: { 'radius-md': '4', 'radius-lg': '6', 'radius-xl': '8' },
          note: '4 / 6 / 8',
        },
        {
          id: 'round',
          label: 'Round',
          values: { 'radius-md': '6', 'radius-lg': '8', 'radius-xl': '12' },
          note: '6 / 8 / 12',
        },
      ],
    },
    {
      id: 'text',
      label: 'Text size',
      tokens: ['text'],
      options: [
        { id: 'standard', label: 'Standard', values: {}, note: '14 px' },
        { id: 'compact', label: 'Compact', values: { text: '13' }, note: '13 px' },
        { id: 'roomy', label: 'Roomy', values: { text: '15' }, note: '15 px' },
        { id: 'large', label: 'Large', values: { text: '16' }, note: '16 px' },
      ],
    },
    {
      id: 'font',
      label: 'Font',
      tokens: ['font'],
      options: FONTS.map((font, i): Preset => ({
        id: font.id,
        label: font.label,
        values: i === 0 ? {} : { font: font.id },
      })),
    },
    {
      id: 'depth',
      label: 'Depth',
      tokens: ['depth'],
      options: [
        { id: 'fluent', label: 'Shadows', values: {} },
        { id: 'none', label: 'Flat', values: { depth: 'none' } },
      ],
    },
  ],
  presets: [{ id: 'current', label: 'Current', values: {} }],
})

/**
 * Fluent's 16 brand shades from one colour, which becomes shade 80 (primary
 * buttons, white text on it). Each shade keeps today's ramp's lightness step
 * and relative chroma, moved to the new colour's lightness, chroma and hue, so
 * Haze's own colour gives back today's ramp.
 */
export function rampFrom(seed: string): BrandVariants {
  const rgb = parseHex(seed)
  if (!rgb) return bayerBrand
  const s = toOklch(rgb)
  const anchor = toOklch(parseHex(bayerBrand[80])!)
  const ramp = { ...bayerBrand }
  for (const slot of SLOTS) {
    if (slot === 80) {
      ramp[slot] = toHex(rgb)
      continue
    }
    const t = toOklch(parseHex(bayerBrand[slot])!)
    const l =
      slot < 80 ? t.l * (s.l / anchor.l) : 1 - (1 - t.l) * ((1 - s.l) / (1 - anchor.l))
    const c = anchor.c > 0 ? t.c * (s.c / anchor.c) : 0
    const h = (t.h + s.h - anchor.h + 360) % 360
    ramp[slot] = toHex(fromOklch({ l, c, h }))
  }
  return ramp
}

const px = (value: string) => `${value}px`

/** The Fluent theme for these overrides; none = the theme as shipped. */
export function themeFor(overrides: Overrides): Theme {
  if (!Object.keys(overrides).length) return bayerLightTheme
  const theme: Theme = overrides.brand
    ? {
        ...createLightTheme(rampFrom(`#${overrides.brand}`)),
        borderRadiusMedium: bayerLightTheme.borderRadiusMedium,
        borderRadiusLarge: bayerLightTheme.borderRadiusLarge,
        borderRadiusXLarge: bayerLightTheme.borderRadiusXLarge,
      }
    : { ...bayerLightTheme }
  if (overrides['radius-md']) theme.borderRadiusMedium = px(overrides['radius-md'])
  if (overrides['radius-lg']) theme.borderRadiusLarge = px(overrides['radius-lg'])
  if (overrides['radius-xl']) theme.borderRadiusXLarge = px(overrides['radius-xl'])
  if (overrides.font) theme.fontFamilyBase = lookAndFeel.cssValue('font', overrides.font)
  const tokens = theme as unknown as Record<string, string>
  if (overrides.text) {
    const scale = Number(overrides.text) / BASE_TEXT_PX
    Object.keys(tokens)
      .filter((name) => /^(fontSize|lineHeight)/.test(name))
      .forEach((name) => {
        tokens[name] = px(String(Math.round(parseFloat(tokens[name]!) * scale)))
      })
  }
  if (overrides.depth === 'none') {
    Object.keys(tokens)
      .filter((name) => /^shadow\d/.test(name))
      .forEach((name) => {
        tokens[name] = 'none'
      })
  }
  return theme
}

/** The CSS variables outside Fluent: `bayerColors`, the sidebar and
 *  index.css's --brand-*. Unset, each falls back to today's value. */
export function cssVarsFor(overrides: Overrides): Record<string, string> {
  const vars: Record<string, string> = {}
  if (overrides.brand) {
    const ramp = rampFrom(`#${overrides.brand}`)
    Object.assign(vars, {
      // navyMid is a white-text button background (EditableField's Save,
      // New demand), so it takes shade 80, the one the contrast chip checks;
      // navy, for headings, sits one darker as it does today.
      '--dac-navy': ramp[70],
      '--dac-navy-mid': ramp[80],
      '--dac-navy-light': ramp[150],
      '--brand-40': ramp[40],
      '--brand-50': ramp[50],
      '--brand-60': ramp[60],
      '--brand-70': ramp[70],
      '--brand-80': ramp[80],
      '--brand-90': ramp[50],
      '--brand-100': ramp[40],
      '--brand-110': ramp[110],
      '--brand-120': ramp[120],
      '--brand-shade-bg': ramp[150],
      '--brand-soft-bg': ramp[140],
      '--brand-stroke': ramp[90],
    })
  }
  if (overrides.sidebar) {
    const bg = `#${overrides.sidebar}`
    Object.assign(vars, {
      '--dac-sidebar-bg': bg,
      '--dac-sidebar-bg-hover': `color-mix(in srgb, ${bg}, white 4%)`,
      '--dac-sidebar-bg-active': `color-mix(in srgb, ${bg}, white 7%)`,
      '--dac-sidebar-border': `color-mix(in srgb, ${bg}, black 25%)`,
    })
  }
  return vars
}

/** What to paste into theme.ts and index.css to make this look the default. */
export function themeSnippet(overrides: Overrides): string {
  const lines = ['// Look & feel → frontend/src/theme.ts']
  if (overrides.brand) {
    const ramp = rampFrom(`#${overrides.brand}`)
    lines.push(
      'export const bayerBrand: BrandVariants = {',
      ...SLOTS.map((slot) => `  ${slot}: '${ramp[slot]}',`),
      '}',
    )
  }
  if (overrides['radius-md'] || overrides['radius-lg'] || overrides['radius-xl']) {
    const theme = themeFor(overrides)
    lines.push(
      'const crispRadii = {',
      `  borderRadiusMedium: '${theme.borderRadiusMedium}',`,
      `  borderRadiusLarge: '${theme.borderRadiusLarge}',`,
      `  borderRadiusXLarge: '${theme.borderRadiusXLarge}',`,
      '} as const',
    )
  }
  if (overrides.font) lines.push(`// fontFamilyBase: '${lookAndFeel.cssValue('font', overrides.font)}'`)
  if (overrides.text) lines.push(`// Body text ${overrides.text} px: scale every fontSize*/lineHeight* token`)
  if (overrides.depth === 'none') lines.push('// Shadows: every shadow* token = none')
  // The colours outside Fluent: --dac-* are the fallbacks in theme.ts
  // (bayerColors, sidebar*), --brand-* are index.css's own :root block.
  const vars = Object.entries(cssVarsFor(overrides))
  if (vars.length) {
    lines.push(
      '',
      '/* Look & feel → --dac-*: fallbacks in theme.ts; --brand-*: frontend/src/index.css */',
      ':root {',
      ...vars.map(([name, value]) => `  ${name}: ${value};`),
      '}',
    )
  }
  return `${lines.join('\n')}\n`
}
